/**
 * Step tracking utilities for tenant screening flow
 * Following best practices from get-verified.tsx and verificationWorkflow schema
 */

// Step names (consistent identifiers)
// Note: Consent is handled separately as a one-time process, not a step
export const STEP_NAMES = {
	PROPERTY: 'property',
	ID_VERIFICATION: 'id-verification',
	RENTAL: 'rental',
	EMPLOYMENT: 'employment',
	VERIFY_INCOME: 'verify-income',
	TENCHECK: 'tencheck',
	REVIEW: 'review',
} as const

// Step display names
export const STEP_DISPLAY_NAMES: Record<string, string> = {
	[STEP_NAMES.PROPERTY]: 'Property',
	[STEP_NAMES.ID_VERIFICATION]: 'ID Verification',
	[STEP_NAMES.RENTAL]: 'Rental',
	[STEP_NAMES.EMPLOYMENT]: 'Employment',
	[STEP_NAMES.VERIFY_INCOME]: 'Verify Income',
	[STEP_NAMES.TENCHECK]: 'TenCheck',
	[STEP_NAMES.REVIEW]: 'Review',
}

// Step name to number mapping
export const stepNameToNumber: Record<string, number> = {
	[STEP_NAMES.PROPERTY]: 1,
	[STEP_NAMES.ID_VERIFICATION]: 2,
	[STEP_NAMES.RENTAL]: 3,
	[STEP_NAMES.EMPLOYMENT]: 4,
	[STEP_NAMES.VERIFY_INCOME]: 5,
	[STEP_NAMES.TENCHECK]: 6,
	[STEP_NAMES.REVIEW]: 7,
}

// Step number to name mapping
export const stepNumberToName: Record<number, string> = {
	1: STEP_NAMES.PROPERTY,
	2: STEP_NAMES.ID_VERIFICATION,
	3: STEP_NAMES.RENTAL,
	4: STEP_NAMES.EMPLOYMENT,
	5: STEP_NAMES.VERIFY_INCOME,
	6: STEP_NAMES.REVIEW,
	7: STEP_NAMES.REVIEW,
}

// Step order array for progress calculation (excludes TenCheck - it's internal/background)
export const STEP_ORDER: string[] = [
	STEP_NAMES.PROPERTY,
	STEP_NAMES.ID_VERIFICATION,
	STEP_NAMES.RENTAL,
	STEP_NAMES.EMPLOYMENT,
	STEP_NAMES.VERIFY_INCOME,
	STEP_NAMES.REVIEW,
]

export const TOTAL_STEPS = STEP_ORDER.length

// UI step number mapping (for user-facing steps, excludes TenCheck)
// Review is step 6 in the UI, but step 7 in the database
export const UI_STEP_NUMBER_TO_NAME: Record<number, string> = {
	1: STEP_NAMES.PROPERTY,
	2: STEP_NAMES.ID_VERIFICATION,
	3: STEP_NAMES.RENTAL,
	4: STEP_NAMES.EMPLOYMENT,
	5: STEP_NAMES.VERIFY_INCOME,
	6: STEP_NAMES.REVIEW,
}

export const UI_STEP_NAME_TO_NUMBER: Record<string, number> = {
	[STEP_NAMES.PROPERTY]: 1,
	[STEP_NAMES.ID_VERIFICATION]: 2,
	[STEP_NAMES.RENTAL]: 3,
	[STEP_NAMES.EMPLOYMENT]: 4,
	[STEP_NAMES.VERIFY_INCOME]: 5,
	[STEP_NAMES.REVIEW]: 6,
}

/**
 * Calculate progress percentage based on completed steps
 */
export function calculateProgress(
	completedSteps: string[],
	totalSteps: number = TOTAL_STEPS,
): number {
	if (totalSteps === 0) return 0
	return Math.round((completedSteps.length / totalSteps) * 100)
}

/**
 * Get the next incomplete step number
 */
export function getNextStep(
	completedSteps: string[],
	currentStep: number,
): number {
	// Find the first incomplete step
	for (let i = 0; i < STEP_ORDER.length; i++) {
		const stepName = STEP_ORDER[i]
		const stepNumber = i + 1
		if (!completedSteps.includes(stepName)) {
			return stepNumber
		}
	}
	// All steps completed, return last step
	return TOTAL_STEPS
}

/**
 * Check if a step is completed
 */
export function isStepCompleted(
	stepName: string,
	completedSteps: string[],
): boolean {
	return completedSteps.includes(stepName)
}

/**
 * Check if user can navigate to a specific step
 * Rules: Can go back to completed steps, cannot skip ahead to incomplete steps
 * Uses UI step numbers (excludes TenCheck)
 */
export function canNavigateToStep(
	stepNumber: number,
	completedSteps: string[],
	currentStep: number,
): boolean {
	// Can always go back
	if (stepNumber < currentStep) {
		return true
	}
	// Can go to current step
	if (stepNumber === currentStep) {
		return true
	}
	// Can go forward only if all previous steps are completed
	// Use UI step mapping (excludes TenCheck)
	const stepName = UI_STEP_NUMBER_TO_NAME[stepNumber]
	if (!stepName) return false

	// Check if all previous steps are completed
	for (let i = 0; i < STEP_ORDER.indexOf(stepName); i++) {
		if (!completedSteps.includes(STEP_ORDER[i])) {
			return false
		}
	}
	return true
}

/**
 * Parse step from URL parameter (supports both number and name)
 * Returns UI step number (excludes TenCheck)
 */
export function parseStepFromUrl(stepParam: string | null): number | null {
	if (!stepParam) return null

	// Try UI step name first (excludes TenCheck)
	if (UI_STEP_NAME_TO_NUMBER[stepParam]) {
		return UI_STEP_NAME_TO_NUMBER[stepParam]
	}

	// Fallback to number (UI step number)
	const numStep = Number.parseInt(stepParam, 10)
	if (!Number.isNaN(numStep) && numStep >= 1 && numStep <= TOTAL_STEPS) {
		return numStep
	}

	return null
}

/**
 * Get step name from UI step number (excludes TenCheck)
 */
export function getStepName(stepNumber: number): string {
	return UI_STEP_NUMBER_TO_NAME[stepNumber] || ''
}

/**
 * Get UI step number from step name (excludes TenCheck)
 */
export function getStepNumber(stepName: string): number {
	return UI_STEP_NAME_TO_NUMBER[stepName] || 1
}

/**
 * Student housing Bank=No marks verify-income completed-but-skipped.
 * Back/forward must hop over that step without rewriting saved data.
 */
export function isVerifyIncomeSkipped(
	stepData?: Record<string, unknown> | null,
): boolean {
	if (!stepData) return false
	return (
		stepData.skipped === true ||
		stepData.skipReason === 'no_canadian_bank_account'
	)
}

export function skippedStepsFromAllStepData(
	allStepData: Record<string, Record<string, unknown> | undefined>,
): string[] {
	return isVerifyIncomeSkipped(allStepData[STEP_NAMES.VERIFY_INCOME])
		? [STEP_NAMES.VERIFY_INCOME]
		: []
}

/** Previous UI step that is still in the flow (skips bypassed steps). */
export function getPreviousUiStep(
	currentStep: number,
	skippedSteps: string[] = [],
): number | null {
	const currentName = UI_STEP_NUMBER_TO_NAME[currentStep]
	if (!currentName) return null
	const idx = STEP_ORDER.indexOf(currentName)
	for (let i = idx - 1; i >= 0; i--) {
		const name = STEP_ORDER[i]
		if (name && !skippedSteps.includes(name)) {
			return UI_STEP_NAME_TO_NUMBER[name] ?? null
		}
	}
	return null
}

/**
 * If the requested step was skipped, hop to the next applicable step in the
 * travel direction. Never mutates step data.
 */
export function resolveUiStepSkippingSkipped(
	requestedStep: number,
	currentStep: number,
	skippedSteps: string[] = [],
): number {
	const requestedName = UI_STEP_NUMBER_TO_NAME[requestedStep]
	if (!requestedName || !skippedSteps.includes(requestedName)) {
		return requestedStep
	}
	if (requestedStep < currentStep) {
		return getPreviousUiStep(currentStep, skippedSteps) ?? requestedStep
	}
	if (requestedStep > currentStep) {
		const idx = STEP_ORDER.indexOf(requestedName)
		for (let i = idx + 1; i < STEP_ORDER.length; i++) {
			const name = STEP_ORDER[i]
			if (name && !skippedSteps.includes(name)) {
				return UI_STEP_NAME_TO_NUMBER[name] ?? requestedStep
			}
		}
	}
	return requestedStep
}

/** Statuses where the applicant must stay on Review — no step Back. */
const STEP_BACK_LOCKED_STATUSES = new Set([
	'submitted',
	'processing',
	'completed',
	'failed',
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
])

/**
 * Step Back / progress-bar jumps are for applications still being filled in
 * (`draft`, `in_progress`, `veriff_pending`, `flinks_pending`, …).
 * Submitted, billing-held, and decision-locked apps stay on Review.
 */
export function canUseStepBackNavigation(input: {
	applicationStatus: string | null | undefined
	billingHold?: boolean
}): boolean {
	if (input.billingHold) return false
	const status = input.applicationStatus
	if (!status) return true
	return !STEP_BACK_LOCKED_STATUSES.has(status)
}

/**
 * Resolve where Back should go.
 * - edit mode → Review (UI step 6)
 * - otherwise → previous non-skipped UI step (null on Property)
 */
export function resolveScreeningBackTarget(input: {
	isEditMode: boolean
	currentStep: number
	skippedSteps?: string[]
}): 'review' | number | null {
	if (input.isEditMode) return 'review'
	return getPreviousUiStep(input.currentStep, input.skippedSteps ?? [])
}

/**
 * Get database step number from step name (includes TenCheck)
 * Use this when saving/retrieving from database
 */
export function getDatabaseStepNumber(stepName: string): number {
	return stepNameToNumber[stepName] || 1
}
